import { variantForModelSelection } from "./model-variant"

export type ModelKey = { providerID: string; modelID: string }

export type SelectionState = {
  agent?: string
  model?: ModelKey
  variant?: string | null
  variants?: Record<string, string | null | undefined>
  source?: "message" | "session" | "user"
  messageID?: string
}

export type SavedSelections = {
  session: Record<string, SelectionState | undefined>
  drafts?: Record<string, SelectionState | undefined>
  draft?: SelectionState
  lastUsed?: SelectionState
}

export function modelKey(model: ModelKey) {
  return `${model.providerID}/${model.modelID}`
}

export function sameModel(a: ModelKey | undefined, b: ModelKey | undefined) {
  return a?.providerID === b?.providerID && a?.modelID === b?.modelID
}

function hasVariant(variants: SelectionState["variants"], key: string) {
  return !!variants && Object.prototype.hasOwnProperty.call(variants, key)
}

/** Reads saved selections; any other shape starts empty. */
export function migrateSavedSelections(value: unknown): SavedSelections {
  if (!value || typeof value !== "object") return { session: {} }
  const item = value as SavedSelections
  if (!item.session || typeof item.session !== "object") return { session: {} }
  return {
    session: item.session,
    drafts: item.drafts,
    draft: item.draft,
    lastUsed: item.lastUsed,
  }
}

export function cloneSelection(value: SelectionState | undefined): SelectionState | undefined {
  if (!value) return undefined
  return {
    ...value,
    model: value.model ? { ...value.model } : undefined,
    variants: value.variants ? { ...value.variants } : undefined,
  }
}

/**
 * A draft starts from this device's last model and agent, which are not yet
 * the user's choice for that draft. Its thinking level starts at the account's
 * level for the model, else the model's default, never the last level used.
 */
export function draftSeed(lastUsed: SelectionState | undefined): SelectionState | undefined {
  const last = cloneSelection(lastUsed)
  return last && { ...last, variant: undefined, variants: undefined, source: undefined }
}

/**
 * A new chat starts at the account's thinking level for each model that has
 * one, until the user changes its selection.
 */
export function withAccountVariants(
  state: SelectionState | undefined,
  defaults: readonly (ModelKey & { variant?: string })[],
): SelectionState | undefined {
  if (defaults.length === 0 || state?.source === "user") return state
  const variants = { ...state?.variants }
  for (const row of defaults) variants[modelKey(row)] = row.variant || null
  const preset = state?.model ? defaults.find((row) => modelKey(row) === modelKey(state.model!)) : undefined
  return { ...state, variants, variant: preset ? preset.variant || null : state?.variant }
}

/** The selection a session's server record implies, keeping the cached per-model levels. */
export function sessionSelection(
  info: { id: string; agent?: string; providerID?: string; modelID?: string; variant?: string },
  cached: SelectionState | undefined,
): SelectionState | undefined {
  if (!info.providerID || !info.modelID) return undefined
  const model = { providerID: info.providerID, modelID: info.modelID }
  const variant = info.variant || null
  return {
    agent: info.agent || "build",
    model,
    variant,
    variants: { ...(cached?.variants ?? {}), [modelKey(model)]: variant },
    source: "session",
    messageID: info.id,
  }
}

/** The selection a sent message implies; an empty message variant keeps the current level. */
export function messageSelection(
  msg: { id: string; agent: string; model: ModelKey; variant?: string },
  current: SelectionState | undefined,
): SelectionState {
  const variant = msg.variant ? msg.variant : current?.variant !== undefined ? current.variant : null
  return {
    agent: msg.agent,
    model: msg.model,
    variant,
    variants: { ...(current?.variants ?? {}), [modelKey(msg.model)]: variant },
    source: "message",
    messageID: msg.id,
  }
}

/**
 * The thinking level a selection names for `active`. A new chat on a model its
 * selection does not name starts at the level remembered for that model.
 */
export function selectedVariant(input: {
  state: SelectionState | undefined
  active: ModelKey | undefined
  session: boolean
}) {
  const { state, active } = input
  const remembered = () => {
    if (input.session || !active || !hasVariant(state?.variants, modelKey(active))) return undefined
    return state!.variants![modelKey(active)]
  }
  if (!state?.model) return state?.variant !== undefined ? state.variant : remembered()
  if (!active) return undefined
  if (!sameModel(state.model, active)) return remembered()
  return state.variant
}

/**
 * The thinking level a model pick lands on. An explicit level (a recent entry
 * carries the one it was used with) wins over what this chat remembered.
 */
export function variantForModelPick(input: {
  state: SelectionState | undefined
  model: ModelKey | undefined
  session: boolean
  explicit?: { variant?: string | null }
}) {
  const { state, model } = input
  if (input.explicit) return input.explicit.variant ?? null
  const key = model ? modelKey(model) : undefined
  const remembered =
    key && hasVariant(state?.variants ?? {}, key)
      ? state?.variants?.[key]
      : model && sameModel(state?.model, model)
        ? state?.variant
        : undefined
  return variantForModelSelection({ session: input.session, selected: remembered })
}

/** Index `direction` steps from `index`, wrapping within `length`. */
export function wrapIndex(index: number, direction: 1 | -1, length: number) {
  const next = index + direction
  if (next < 0) return length - 1
  if (next >= length) return 0
  return next
}

/** Parses a `provider/model` reference; undefined without a provider. */
export function parseModelRef(value: string): ModelKey | undefined {
  const split = value.indexOf("/")
  if (split <= 0) return undefined
  return { providerID: value.slice(0, split), modelID: value.slice(split + 1) }
}
